import { useEffect, useRef, useState } from "react";

// Structural copy of the voice participant shape (components define their own types, no imports from features/).
type VoiceTileParticipant = { id: string; name: string; stream: MediaStream | null; avatar?: string | null; isSelf?: boolean; speaking?: boolean; muted?: boolean };

export default function VoiceTile({ participant, volumeFor, onVolumeChange }: { participant: VoiceTileParticipant; volumeFor: (id: string) => number; onVolumeChange: (id: string, v: number) => void }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [volumeOpen, setVolumeOpen] = useState(false);
  useEffect(() => {
    if (videoRef.current && participant.stream) {
      (videoRef.current as HTMLVideoElement).srcObject = participant.stream;
      (videoRef.current as HTMLVideoElement).play().catch(()=>{});
    }
  }, [participant.stream, participant.id]);
  const status = participant.muted ? "Muted" : participant.speaking ? "Speaking" : participant.isSelf ? "Connected" : "Available";
  const currentVolume = volumeFor(participant.id);
  const volumePct = Math.round(currentVolume * 100);
  return (
    <div
      className={`group relative flex w-32 flex-col items-center gap-3 rounded-[28px] px-3 py-4 text-center transition duration-200 sm:w-40 ${participant.stream ? "bg-black/20 sm:w-52" : "bg-white/[0.02] hover:bg-white/[0.04]"}`}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setVolumeOpen(false); }}
    >
      {/* Volume popover */}
      {volumeOpen && !participant.isSelf && (
        <div className="absolute bottom-[calc(100%+8px)] left-1/2 z-50 -translate-x-1/2 w-44 rounded-2xl border border-white/10 bg-neutral-900/95 p-3 shadow-2xl backdrop-blur-xl">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-meta text-fg-3">Volume</span>
            <span className="text-meta font-medium text-emerald-300">{volumePct}%</span>
          </div>
          <input
            type="range"
            min={0}
            max={200}
            step={5}
            value={volumePct}
            className="w-full accent-emerald-400 cursor-pointer"
            onChange={(e) => onVolumeChange(participant.id, Number(e.target.value) / 100)}
            onMouseDown={(e) => e.stopPropagation()}
          />
          <div className="mt-2 flex justify-between text-meta text-fg-3">
            <span>0%</span>
            <span className="text-fg-3">100%</span>
            <span>200%</span>
          </div>
        </div>
      )}
      <div
        className={`relative flex items-center justify-center ${participant.stream ? "w-full" : ""} ${!participant.isSelf ? "cursor-pointer" : ""}`}
        onClick={() => { if (!participant.isSelf) setVolumeOpen((v) => !v); }}
        onKeyDown={(e) => {
          if (!participant.isSelf && e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setVolumeOpen((v) => !v);
          }
        }}
        role={!participant.isSelf ? "button" : undefined}
        tabIndex={!participant.isSelf ? 0 : undefined}
        aria-label={!participant.isSelf ? `Adjust volume for ${participant.name}` : undefined}
        aria-expanded={!participant.isSelf ? volumeOpen : undefined}
        title={!participant.isSelf ? "Click to adjust volume" : undefined}
      >

        <div className={`${participant.stream ? "relative h-28 w-full rounded-2xl sm:h-32" : "relative h-20 w-20 rounded-full sm:h-24 sm:w-24"} overflow-hidden bg-gradient-to-br from-white/10 to-black/30 flex items-center justify-center ${participant.speaking ? "ring-2 ring-emerald-400" : "ring-1 ring-white/10"}`}>
        {participant.stream ? (
          <video ref={videoRef} autoPlay playsInline muted={participant.isSelf} className="w-full h-full object-cover" />
        ) : participant.avatar ? (
          <img src={participant.avatar} alt={participant.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-2xl font-medium text-fg-2">
            {(participant.name || 'U').slice(0,1).toUpperCase()}
          </div>
        )}
        </div>
        {/* Volume indicator badge — shown when not default */}
        {!participant.isSelf && currentVolume !== 1 && (
          <div className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full border border-white/10 bg-neutral-900 text-[0.5625rem] font-medium text-emerald-300">
            {volumePct > 100 ? '↑' : volumePct === 0 ? '✕' : '↓'}
          </div>
        )}
      </div>
      <div className="flex max-w-full flex-col items-center gap-1 text-sm text-fg">
        <span className="max-w-full truncate font-medium">{participant.name}</span>
        <span className={`inline-flex items-center gap-1.5 text-meta ${participant.speaking ? "text-emerald-300" : participant.muted ? "text-rose-300/90" : "text-fg-3"}`}>
          <span className={`h-1.5 w-1.5 rounded-full ${participant.speaking ? "bg-emerald-400 animate-pulse" : participant.muted ? "bg-rose-400/80" : "bg-white/25"}`} />
          {status}
        </span>
      </div>
    </div>
  );
}
